import React from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { NotFoundPage } from 'app/components/NotFoundPage/Loadable';
import { UsherProfile } from 'app/components/UsherProfile/Loadable';
import { Dashboard as AttendanceHistoryDashboard } from 'app/pages/AttendanceHistory/Dashboard';
import { AttendanceHistory } from 'app/pages/AttendanceHistory/Loadable';
import { LoginForm } from 'app/pages/Auth/Login/Loadable';
import { HomePage } from 'app/pages/HomePage/Loadable';
import { InitiativeDetails } from 'app/pages/InitiativeDetails/Loadable';
import { InitiativeManagement } from 'app/pages/InitiativeManagement/Loadable';
import { Levels } from 'app/pages/Levels/Loadable';
import { Steps } from 'app/pages/Steps/Loadable';
import { Tags } from 'app/pages/Tags/Loadable';
import { TimeSlots } from 'app/pages/TimeSlots/Loadable';
import { NewSlotForm } from 'app/pages/TimeSlots/NewSlotForm';
import { Ushers } from 'app/pages/Ushers/Loadable';

import { useAuth } from 'app/hooks/useAuth';

import AddResultsForm from 'app/components/Results/AddResultsForm';
import EditResultsForm from 'app/components/Results/EditResultsForm';
import CommunicationHistoryPage from 'app/pages/CommunicationHistory';
import GuideStatusPage from 'app/pages/GuideStatus';
import InitiativeEdit from 'app/pages/InitiativeEdit';
import LocationsList from 'app/pages/Locations';
import NewInitiative from 'app/pages/NewInitiative';
import ProfilePage from 'app/pages/ProfilePage';
import ResultsPage from 'app/pages/ResultsPage';
import UsherHistoryPage from 'app/pages/UsherHistory';
import { MarkAttendancePage } from './pages/AttendanceHistory/MarkAttendance/Loadable';
import { DownloadRequests } from './pages/DownloadRequests/Loadable';

// can be accessed by anyone, whether logged in or not.
const UnprotectedRoutes = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) return children;
  else navigate('/');
};

// require the user to be authenticated.
const ProtectedRoutes = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) return children;
  else navigate('/login');
};

export function AppRoutes() {
  return (
    <div className="m-2 m-md-5">
      <Routes>
        <Route
          path="/results/:resultsID/view/"
          element={
            <ProtectedRoutes>
              <ResultsPage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/results/:resultsID/edit/"
          element={
            <ProtectedRoutes>
              <EditResultsForm />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/results/:initiativeUsherID/add/:initiativeUsherStepID/"
          element={
            <ProtectedRoutes>
              <AddResultsForm />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/communication/:initiativeUsherID/history/"
          element={
            <ProtectedRoutes>
              <CommunicationHistoryPage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/ushers/:usherUserID/history/"
          element={
            <ProtectedRoutes>
              <UsherHistoryPage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/ushers/:usherID/profile/"
          element={
            <ProtectedRoutes>
              <UsherProfile />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/profile/"
          element={
            <ProtectedRoutes>
              <ProfilePage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/attendance/"
          element={
            <ProtectedRoutes>
              <AttendanceHistoryDashboard />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/attendance/:initiativeUsherID/history/"
          element={
            <ProtectedRoutes>
              <AttendanceHistory />
            </ProtectedRoutes>
          }
        />
        <Route
          path="/attendance/mark-attendance/"
          element={
            <ProtectedRoutes>
              <MarkAttendancePage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/locations"
          element={
            <ProtectedRoutes>
              <LocationsList />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives/:initiativeID/slots/"
          element={
            <ProtectedRoutes>
              <TimeSlots />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives/:initiativeID/slots/add/"
          element={
            <ProtectedRoutes>
              <NewSlotForm />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/guide-status/"
          element={
            <ProtectedRoutes>
              <GuideStatusPage />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/tags/"
          element={
            <ProtectedRoutes>
              <Tags />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/levels/"
          element={
            <ProtectedRoutes>
              <Levels />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives-management/"
          element={
            <ProtectedRoutes>
              <InitiativeManagement />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/download-requests/"
          element={
            <ProtectedRoutes>
              <DownloadRequests />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/ushers/"
          element={
            <ProtectedRoutes>
              <Ushers />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/steps/"
          element={
            <ProtectedRoutes>
              <Steps />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives/add/"
          element={
            <ProtectedRoutes>
              <NewInitiative />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives/:initiativeID/edit/"
          element={
            <ProtectedRoutes>
              <InitiativeEdit />
            </ProtectedRoutes>
          }
        />

        <Route
          path="/initiatives/:initiativeID/"
          element={<InitiativeDetails />}
        />

        <Route
          path="/login/"
          element={
            <UnprotectedRoutes>
              <LoginForm />
            </UnprotectedRoutes>
          }
        />

        <Route path="/" element={<HomePage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}
